import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AutoBreadcrumb } from "@/components/ui/auto-breadcrumb";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/auth-context";
import { getCurrencyName, getCurrencySymbol } from "@/lib/currency";
import { getExchangeLabel } from "@/lib/exchange-mapping";
import {
  createAdminAsset,
  getAdminAssetFormOptions,
  validateAdminAsset,
  type AdminAssetFormOptions,
  type AdminAssetPayload,
} from "@/services/admin-asset-service";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

const assetTypeSchema = z.enum(["stock", "etf", "crypto", "fund"]);

const formSchema = z
  .object({
    type: assetTypeSchema,
    name: z.string().min(1, "Name is required"),
    symbol: z.string().min(1, "Symbol is required"),
    exchange: z.string().optional(),
    currency: z.string().optional(),
    isin: z.string().optional(),
    platform: z.string().optional(),
    contract_addr: z.string().optional(),
    sync_after_create: z.boolean().default(true),
  })
  .superRefine((values, ctx) => {
    if ((values.type === "stock" || values.type === "etf" || values.type === "fund") && !values.exchange?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["exchange"],
        message: "Exchange is required for stocks, ETFs, and funds",
      });
    }
    if (values.type === "fund" && !values.currency?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["currency"],
        message: "Currency is required for funds",
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

export const Route = createFileRoute("/_authenticated/admin/assets")({
  component: AdminAssetsPage,
});

function AdminAssetsPage() {
  const { user } = useAuth();
  const [options, setOptions] = useState<AdminAssetFormOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdSummary, setCreatedSummary] = useState<{
    assetId: string;
    hydrationRequested: boolean;
    hydrationError?: string;
  } | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: "stock",
      name: "",
      symbol: "",
      exchange: "",
      currency: "",
      isin: "",
      platform: "",
      contract_addr: "",
      sync_after_create: true,
    },
  });

  const assetType = form.watch("type");
  const currencyOptions = useMemo(
    () =>
      [...(options?.fiat_currencies ?? [])].sort((a, b) =>
        a.symbol.localeCompare(b.symbol)
      ),
    [options?.fiat_currencies]
  );
  const exchangeOptions = useMemo(
    () => (options?.exchanges ?? []).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [options?.exchanges]
  );

  const trimOnBlur = (currentValue: string, onChange: (value: string) => void, onBlur: () => void) => {
    onBlur();
    const trimmedValue = currentValue.trim();
    if (trimmedValue !== currentValue) {
      onChange(trimmedValue);
    }
  };

  const loadOptions = useCallback(async () => {
    try {
      setLoadingOptions(true);
      const data = await getAdminAssetFormOptions();
      setOptions(data);
    } catch (error) {
      toast.error("Failed to load asset admin options", {
        description: (error as Error).message,
      });
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) {
      setLoadingOptions(false);
      return;
    }
    void loadOptions();
  }, [loadOptions, user?.is_admin]);

  const buildPayload = (values: FormValues): AdminAssetPayload => {
    const trim = (value?: string) => value?.trim() || undefined;

    return {
      type: values.type,
      name: values.name.trim(),
      symbol: values.symbol.trim(),
      exchange: trim(values.exchange),
      currency: trim(values.currency),
      isin: trim(values.isin),
      platform: trim(values.platform),
      contract_addr: trim(values.contract_addr),
      sync_after_create: values.sync_after_create,
    };
  };

  const handleValidate = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    try {
      setValidating(true);
      setValidationErrors([]);
      const result = await validateAdminAsset(buildPayload(form.getValues()));
      if (result.valid) {
        toast.success("Asset validation passed");
        setValidationErrors([]);
      } else {
        setValidationErrors(result.errors ?? ["Asset validation failed"]);
        toast.error("Asset validation failed");
      }
    } catch (error) {
      toast.error("Validation request failed", {
        description: (error as Error).message,
      });
    } finally {
      setValidating(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      setSubmitting(true);
      setValidationErrors([]);
      setCreatedSummary(null);

      const result = await createAdminAsset(buildPayload(values));
      setCreatedSummary({
        assetId: result.asset.asset_id,
        hydrationRequested: result.hydration_requested,
        hydrationError: result.hydration_request_error,
      });

      if (result.hydration_requested) {
        toast.success("Asset created and hydration requested");
      } else if (result.hydration_request_error) {
        toast.warning("Asset created but hydration could not be requested", {
          description: result.hydration_request_error,
        });
      } else {
        toast.success("Asset created");
      }

      form.reset({
        ...form.getValues(),
        name: "",
        symbol: "",
        exchange: assetType === "crypto" ? "" : form.getValues("exchange"),
        isin: "",
        platform: "",
        contract_addr: "",
      });
    } catch (error) {
      toast.error("Failed to create asset", {
        description: (error as Error).message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="flex flex-col h-screen">
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <AutoBreadcrumb />
          <Card className="mt-6 max-w-2xl p-6">
            <h1 className="text-xl font-semibold">Admin access required</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              You do not have permission to view this page.
            </p>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <main className="flex-1 overflow-auto p-4 md:p-6">
        <AutoBreadcrumb />
        <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
          <Card className="overflow-hidden">
            <CardHeader className="border-b bg-muted/20">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle>Create Admin Asset</CardTitle>
                  <CardDescription className="mt-2 max-w-2xl">
                    Create a stock, ETF, fund, or crypto asset with only the fields the
                    collector needs at runtime. Hydration stays event-driven through RabbitMQ.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void loadOptions()}
                  disabled={loadingOptions}
                >
                  {loadingOptions ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <Form {...form}>
                <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Asset Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="stock">Stock</SelectItem>
                              <SelectItem value="etf">ETF</SelectItem>
                              <SelectItem value="fund">Fund</SelectItem>
                              <SelectItem value="crypto">Crypto</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Only asset types supported by the collector are exposed here.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="symbol"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Symbol</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="AAPL, VOO, BTC"
                              {...field}
                              onBlur={() => trimOnBlur(field.value, field.onChange, field.onBlur)}
                            />
                          </FormControl>
                          <FormDescription>
                            This remains the canonical symbol used by the product and by Yahoo sync.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Apple Inc."
                            {...field}
                            onBlur={() => trimOnBlur(field.value, field.onChange, field.onBlur)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {(assetType === "stock" || assetType === "etf" || assetType === "fund") && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="exchange"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Exchange</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value || undefined}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an exchange" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {exchangeOptions.map((exchange) => (
                                  <SelectItem key={exchange} value={exchange}>
                                    {exchange} · {getExchangeLabel(exchange)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Required because the collector rebuilds the Yahoo symbol from
                              `symbol + exchange`. Example: `0P0001UBP6` + `F` becomes `0P0001UBP6.F`.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="isin"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>ISIN</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Optional metadata"
                                {...field}
                                onBlur={() => trimOnBlur(field.value ?? "", field.onChange, field.onBlur)}
                              />
                            </FormControl>
                            <FormDescription>
                            Optional for runtime hydration, useful for import and matching.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  )}

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="currency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reference Fiat</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Optional but recommended" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {currencyOptions.map((currency) => (
                                <SelectItem key={currency.asset_id} value={currency.symbol}>
                                  {currency.symbol} · {getCurrencyName(currency.symbol)} · {getCurrencySymbol(currency.symbol)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Required for funds. Optional but useful for richer product data elsewhere.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {assetType === "crypto" ? (
                      <FormField
                        control={form.control}
                        name="platform"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Platform</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Ethereum, Base, Solana..."
                                {...field}
                                onBlur={() => trimOnBlur(field.value ?? "", field.onChange, field.onBlur)}
                              />
                            </FormControl>
                            <FormDescription>
                              Optional metadata for future manual crypto disambiguation.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : (
                      <div className="rounded-lg border border-dashed bg-muted/15 p-4">
                        <p className="text-sm font-medium">Collector reminder</p>
                        <p className="mt-2 text-sm text-muted-foreground">
                          Charts hydration will still fail if Yahoo returns a quote currency
                          that does not exist as a fiat asset in Mongo.
                        </p>
                      </div>
                    )}
                  </div>

                  {assetType === "crypto" && (
                    <FormField
                      control={form.control}
                      name="contract_addr"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contract Address</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Optional on-chain contract address"
                              {...field}
                              onBlur={() => trimOnBlur(field.value ?? "", field.onChange, field.onBlur)}
                            />
                          </FormControl>
                          <FormDescription>
                            Optional metadata only. Not required for hydration.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="sync_after_create"
                    render={({ field }) => (
                      <FormItem className="rounded-lg border bg-muted/10 p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="space-y-1">
                            <FormLabel>Synchronize after creation</FormLabel>
                            <FormDescription>
                              Publish `asset.hydration_requested` immediately after insertion.
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </div>
                      </FormItem>
                    )}
                  />

                  {validationErrors.length > 0 && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                        <Sparkles className="h-4 w-4" />
                        Validation issues
                      </div>
                      <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-destructive">
                        {validationErrors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleValidate()}
                      disabled={validating || submitting}
                    >
                      {validating ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Validate
                    </Button>
                    <Button type="submit" disabled={submitting || loadingOptions}>
                      {submitting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Create Asset
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Creation Rules</CardTitle>
                <CardDescription>
                  The form only enforces what the collector actually needs at runtime.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div>
                  <Label className="text-foreground">Stocks / ETFs</Label>
                  <p className="mt-1">Require `symbol` and `exchange` for hydration.</p>
                </div>
                <div>
                  <Label className="text-foreground">Funds</Label>
                  <p className="mt-1">Require `symbol`, `exchange`, and `currency`. Split Yahoo tickers like `0P0001UBP6.F` into `symbol=0P0001UBP6` and `exchange=F`.</p>
                </div>
                <div>
                  <Label className="text-foreground">Crypto</Label>
                  <p className="mt-1">No exchange required. Yahoo quote currency still needs a fiat asset in Mongo.</p>
                </div>
                <div>
                  <Label className="text-foreground">Optional metadata</Label>
                  <p className="mt-1">`isin`, `exchange`, `platform`, and `contract_addr` improve product quality when they are not already mandatory.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Available Options</CardTitle>
                <CardDescription>
                  Pulled from the current database so admin choices stay aligned with production data.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="mb-2 text-sm font-medium">Fiat assets</p>
                  <div className="flex flex-wrap gap-2">
                    {currencyOptions.map((currency) => (
                      <Badge key={currency.asset_id} variant="secondary">
                        {currency.symbol} · {getCurrencyName(currency.symbol)}
                      </Badge>
                    ))}
                    {currencyOptions.length === 0 && (
                      <span className="text-sm text-muted-foreground">No fiat assets found.</span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-sm font-medium">Known exchanges</p>
                  <div className="flex flex-wrap gap-2">
                    {exchangeOptions.map((exchange) => (
                      <Badge key={exchange} variant="outline">
                        {exchange} · {getExchangeLabel(exchange)}
                      </Badge>
                    ))}
                    {exchangeOptions.length === 0 && (
                      <span className="text-sm text-muted-foreground">No exchange options found.</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {createdSummary && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Latest Result</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p>
                    <span className="font-medium">Asset ID:</span> {createdSummary.assetId}
                  </p>
                  <p>
                    <span className="font-medium">Hydration requested:</span>{" "}
                    {createdSummary.hydrationRequested ? "yes" : "no"}
                  </p>
                  {createdSummary.hydrationError ? (
                    <p className="text-amber-600">
                      <span className="font-medium">Hydration warning:</span>{" "}
                      {createdSummary.hydrationError}
                    </p>
                  ) : null}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
